import { format } from 'date-fns'

const MIN_YEAR = 1900
const MAX_YEAR = 2100

/**
 * Expand a 2-digit year using a 1930–2029 window:
 * 00–29 → 2000–2029, 30–99 → 1930–1999.
 */
function expandTwoDigitYear(year: number): number {
  return year <= 29 ? 2000 + year : 1900 + year
}

function calendarDate(year: number, month1: number, day: number): Date | undefined {
  if (year < MIN_YEAR || year > MAX_YEAR) return undefined
  if (month1 < 1 || month1 > 12 || day < 1 || day > 31) return undefined

  const date = new Date(year, month1 - 1, day)
  if (date.getFullYear() !== year || date.getMonth() !== month1 - 1 || date.getDate() !== day) {
    return undefined
  }
  return date
}

/**
 * Parse a user-typed date into a local calendar `Date` (year/month/day only).
 * Accepts `MM/DD/YYYY`, `M/D/YYYY`, dashes/dots, `YYYY-MM-DD`, `MMDDYYYY`,
 * and 2-digit years.
 */
export function parseTypedDate(input: string): Date | undefined {
  const trimmed = input.trim()
  if (!trimmed) return undefined

  const iso = trimmed.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/)
  if (iso) {
    return calendarDate(Number(iso[1]), Number(iso[2]), Number(iso[3]))
  }

  const us = trimmed.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{2}|\d{4})$/)
  if (us) {
    const yearRaw = us[3]
    if (!yearRaw) return undefined
    const year = yearRaw.length === 2 ? expandTwoDigitYear(Number(yearRaw)) : Number(yearRaw)
    return calendarDate(year, Number(us[1]), Number(us[2]))
  }

  if (/^\d{8}$/.test(trimmed)) {
    return calendarDate(
      Number(trimmed.slice(4, 8)),
      Number(trimmed.slice(0, 2)),
      Number(trimmed.slice(2, 4)),
    )
  }

  return undefined
}

export function formatTypedDate(date: Date): string {
  return format(date, 'MM/dd/yyyy')
}
